import { Flex, MultiSelect, MultiSelectProps, Text } from "@mantine/core";
import { forwardRef, useMemo, useState } from "react";

import { FKInfoLabel } from "@/features/ui/Base/List/CustomFilter/MultiField";
import { IconX } from "@tabler/icons-react";
import { useMoldsMultiAutoComplete, useMoldsMultiCode } from "./multiAuto-useGetMoldsQuery";

interface MoldMultiProps extends React.ComponentPropsWithoutRef<"div"> {
  label: string;
  value: string;
}

export interface MoldsMultiAutoCompleteProps
  extends Partial<MultiSelectProps>,
    Partial<React.RefAttributes<HTMLInputElement>> {
  value: string[];
  onChange?: (e: string[]) => void; // (value: string[]) => React.Dispatch<React.SetStateAction<string>>;
  maxDropdownHeight?: number;
  width?: string;
}

export const MoldsMultiAutoComplete = (params: MoldsMultiAutoCompleteProps) => {
  const { value: molds, onChange, maxDropdownHeight, width, ...etcParams } = params;
  const [focused, setFocused] = useState<boolean>(true);
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const { data: options } = useMoldsMultiAutoComplete(focused, searchKeyword);
  const { data: initialOptions } = useMoldsMultiCode(!!searchKeyword, searchKeyword ?? null);

  const onChangeHandler = (e: string[]) => {
    onChange && onChange(e);
  };

  const SelectMold = forwardRef<HTMLDivElement, MoldMultiProps>(
    ({ label: name, value: code, ...others }, ref) => (
      <div ref={ref} {...others}>
        <Flex direction="row" justify="space-between" align="center">
          <Flex align={"center"}>
            <Text>{name}</Text>
            <Text fz="xs">(Color명: {code})</Text>
          </Flex>
        </Flex>
      </div>
    ),
  );

  // data prop에 들어가는 중복 제거된 data를 useMemo로 감싸기
  const filteredOptions = useMemo(() => {
    const combinedOptions = [
      ...(options ?? []),
      ...(initialOptions ?? []),
      ...(molds.map((value) => ({ label: value, value })) ?? []),
    ];

    return combinedOptions.reduce(
      (unique: MoldMultiProps[], option: MoldMultiProps) =>
        unique.some((u) => u.value === option.value) ? unique : [...unique, option],
      [],
    );
  }, [options, initialOptions, molds]);

  return (
    <MultiSelect
      clearable
      value={molds}
      data={filteredOptions}
      onChange={onChangeHandler}
      searchable
      itemComponent={SelectMold}
      searchValue={searchKeyword}
      withinPortal
      onDropdownOpen={() => setFocused(true)}
      onDropdownClose={() => setFocused(false)}
      maxDropdownHeight={maxDropdownHeight ?? 150}
      onSearchChange={setSearchKeyword}
      nothingFound="No Options"
      rightSection={MoldInfo({
        Mold: molds,
        onChange: onChangeHandler,
      })}
      {...etcParams}
    />
  );
};

const MoldInfo = (params: { Mold?: string[]; onChange: (e: string[]) => void }) => {
  const { Mold, onChange } = params;

  const clearHandler: React.MouseEventHandler<SVGSVGElement> = (e) => {
    e.stopPropagation();
    e.preventDefault();
    onChange([]);
  };

  return Mold?.length ? (
    <FKInfoLabel>
      <IconX size="1rem" onClick={clearHandler} />
    </FKInfoLabel>
  ) : null;
};
